import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  SimpleGrid,
  HStack,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { FiUsers, FiAlertTriangle, FiAlertCircle, FiFilter } from 'react-icons/fi';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { BiGridAlt } from 'react-icons/bi';
import SmartFilterBar from 'components/SmartFilterBar';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import { downloadExcel } from 'react-export-table-to-excel';
import MonthlyTable from './components/MonthlyTable';
import CarteraTable from './components/CarteraTable';

const currentYear = new Date().getFullYear();

const MONTH_LABELS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const PAGE_SIZE = 10;
const currentMonth1Based = new Date().getMonth() + 1;

export default function ReportePagos() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    program_type: searchParams.get('program_type') || '',
    program: searchParams.get('program') || '',
    group: searchParams.get('group') || '',
    status: searchParams.get('status') || '',
    semester: searchParams.get('semester') || '',
  }));
  const [report, setReport] = useState({ data: [], meta: null });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    const n = parseInt(p, 10);
    return n >= 1 ? n : 1;
  });

  const hasMinimumFilters = Boolean(filters.program_type);

  const syncFiltersToUrl = useCallback(
    (nextFilters, page) => {
      const next = new URLSearchParams();
      if (nextFilters.program_type) next.set('program_type', nextFilters.program_type);
      if (nextFilters.program) next.set('program', nextFilters.program);
      if (nextFilters.group) next.set('group', nextFilters.group);
      if (nextFilters.status) next.set('status', nextFilters.status);
      if (nextFilters.semester) next.set('semester', nextFilters.semester);
      if (page > 1) next.set('page', String(page));
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadReport = useCallback(async () => {
    if (!hasMinimumFilters) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
      });
      if (filters.program) params.set('program', filters.program);
      if (filters.group) params.set('group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.semester) params.set('semester', filters.semester);
      params.set('year', String(currentYear));
      const res = await xhr(`/payments/report-status?${params.toString()}`);
      const body = res.data || res;
      setReport({ data: body.data || [], meta: body.meta || null });
    } catch (err) {
      console.error('Error loading report:', err);
      setReport({ data: [], meta: null });
    } finally {
      setLoading(false);
    }
  }, [hasMinimumFilters, filters, currentPage]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
      syncFiltersToUrl(newFilters, 1);
    },
    [syncFiltersToUrl]
  );

  const handlePageChange = useCallback(
    (event) => {
      const page = event.selected + 1;
      setCurrentPage(page);
      syncFiltersToUrl(filters, page);
    },
    [filters, syncFiltersToUrl]
  );

  const handleDownloadExcel = useCallback(async () => {
    if (!hasMinimumFilters) return;
    setDownloading(true);
    try {
      const baseParams = new URLSearchParams({
        program_type: filters.program_type,
        page: '1',
        pageSize: '100',
        year: String(currentYear),
      });
      if (filters.program) baseParams.set('program', filters.program);
      if (filters.group) baseParams.set('group', filters.group);
      if (filters.status) baseParams.set('status', filters.status);
      if (filters.semester) baseParams.set('semester', filters.semester);
      baseParams.set('include_first_payment_date', '1');
      baseParams.set('include_subjects', '1');

      const first = await xhr(`/payments/report-status?${baseParams.toString()}`);
      const firstBody = first.data || first;
      const total = firstBody.meta?.pagination?.total ?? 0;
      const pageSize = 100;
      const pageCount = Math.ceil(total / pageSize) || 1;
      const isSemesterBasedExport = firstBody.meta?.isSemesterBased === true;

      let allRows = [...(firstBody.data || [])];
      for (let page = 2; page <= pageCount; page++) {
        baseParams.set('page', String(page));
        const res = await xhr(`/payments/report-status?${baseParams.toString()}`);
        const data = (res.data || res).data || [];
        allRows = allRows.concat(data);
      }

      function monthStatusToLabel(status, month) {
        if (status === 'PAID') return 'Pagado';
        if (status === 'LATE') return 'Vencido';
        if (status === 'N/A') return 'No aplica';
        if (status === 'PENDING' && month === currentMonth1Based) return 'Próximo a vencer';
        if (status === 'PENDING') return '—';
        return status || '—';
      }

      let programLabel = '—';
      if (filters.program && filters.program_type) {
        try {
          const ptRes = await xhr('/program-types?filters[active][$eq]=true');
          const ptList = (ptRes.data?.data || []).map((el) => ({ ...el, value: el.slug }));
          const programType = ptList.find((p) => p.value === filters.program_type);
          if (programType) {
            const base = `/${programType.type.replace('_', '-')}s`;
            const prRes = await xhr(
              `${base}?filters[program_type][id][$eq]=${programType.id}&filters[active][$eq]=true`
            );
            const program = (prRes.data?.data || []).find((p) => p.id === Number(filters.program));
            if (program?.name) programLabel = program.name;
          }
        } catch (_) {}
      }

      let parentMap = {};
      const studentIds = allRows.map((r) => r.student?.id).filter(Boolean);
      if (studentIds.length) {
        try {
          const parentScore = (p) => {
            if (!p) return 0;
            let score = 0;
            if (p.full_name) score += 3;
            if (p.home_phone) score += 3;
            if (p.email) score += 2;
            if (p?.user?.first_name || p?.user?.last_name) score += 1;
            if (p?.user?.cellphone) score += 1;
            return score;
          };

          const setParentForStudent = (studentId, parent) => {
            if (!studentId || !parent) return;
            const current = parentMap[studentId];
            if (!current || parentScore(parent) > parentScore(current)) {
              parentMap[studentId] = parent;
            }
          };

          const parentsUrlForStudentIds = (ids) => {
            const params = new URLSearchParams();
            params.set('populate', '*');
            ids.forEach((sid, index) => {
              params.append(`filters[students][id][$in][${index}]`, String(sid));
            });
            return `/parents?${params.toString()}`;
          };

          const parentsUrlForOneStudent = (sid) => {
            const params = new URLSearchParams();
            params.set('populate', '*');
            params.set('filters[students][id][$eq]', String(sid));
            return `/parents?${params.toString()}`;
          };

          const normalizeParents = (res) => {
            const payload = res?.data ?? res;
            const list = Array.isArray(payload) ? payload : payload?.data || [];
            return list.map((p) => {
              if (!p?.attributes) return p;
              const flat = { id: p.id, ...p.attributes };
              if (flat.user?.data) {
                const u = flat.user.data;
                flat.user = u?.attributes ? { id: u.id, ...u.attributes } : u;
              }
              return flat;
            });
          };

          const fillParentMap = (parents) => {
            parents.forEach((p) => {
              const relatedStudents = Array.isArray(p?.students?.data)
                ? p.students.data
                : Array.isArray(p?.students)
                  ? p.students
                  : [];
              relatedStudents.forEach((st) => {
                if (typeof st === 'number') {
                  setParentForStudent(st, p);
                  return;
                }
                const normalizedStudent = st?.attributes ? { id: st.id, ...st.attributes } : st;
                const sid = normalizedStudent?.id ?? st?.id;
                if (sid != null) setParentForStudent(sid, p);
              });
            });
          };

          const uniqueStudentIds = [...new Set(studentIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)))];

          const IN_CHUNK = 40;
          for (let i = 0; i < uniqueStudentIds.length; i += IN_CHUNK) {
            const chunk = uniqueStudentIds.slice(i, i + IN_CHUNK);
            const parentsRes = await xhr(parentsUrlForStudentIds(chunk));
            fillParentMap(normalizeParents(parentsRes));
          }

          const missing = uniqueStudentIds.filter((id) => !parentMap[id]);
          if (missing.length) {
            for (const id of missing) {
              const res = await xhr(parentsUrlForOneStudent(id));
              const normalized = normalizeParents(res);
              fillParentMap(normalized);
              if (!parentMap[id] && normalized.length) {
                setParentForStudent(id, normalized[0]);
              }
            }
          }
        } catch (err) {
          console.error('Error cargando acudientes para Excel reporte-pagos:', err);
        }
      }

      const STATUS_LABEL = {
        AL_DIA: 'Al día',
        PROXIMO_A_VENCER: 'Próximo a vencer',
        EN_MORA: 'En mora',
        SIN_PAGOS: 'Sin pagos',
        REVISION_PAUSA: 'Revisar historial de pausa',
      };

      const body = allRows.map((row) => {
        const u = row.student?.user || {};
        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
        const parent = parentMap[row.student?.id] || null;
        const parentNameVal = parent?.full_name
          || `${parent?.user?.first_name || ''} ${parent?.user?.last_name || ''}`.trim()
          || '—';
        const parentPhoneVal = parent?.home_phone || parent?.user?.cellphone || '—';
        const firstPayLabel = row.firstPaymentDate
          ? dayjs(row.firstPaymentDate).format('DD-MM-YYYY')
          : '—';
        // Solo aplica si el tipo de programa tiene activo "derechos de grado".
        const gradFeeLabel = row.hasGraduationFee ? (row.graduationFeePaid ? 'Sí' : 'No') : '—';

        if (isSemesterBasedExport) {
          const lastPayLabel = row.lastPaymentDate
            ? dayjs(row.lastPaymentDate).format('DD-MM-YYYY')
            : '—';
          const statusLabel = STATUS_LABEL[row.financialStatus] || '—';
          // Inicio real (primera materia activada; fallback primer pago).
          const startLabel = row.startDate
            ? dayjs(row.startDate).format('DD-MM-YYYY')
            : firstPayLabel;
          return [
            row.student?.id ?? '—',
            fullName,
            u.cellphone ?? '—',
            u.email ?? '—',
            parentNameVal,
            parentPhoneVal,
            row.programName ?? programLabel,
            row.semesterName ?? '—',
            startLabel,
            lastPayLabel,
            row.paidSubjectsCount ?? 0,
            row.activatedSubjectsCount ?? 0,
            statusLabel,
            gradFeeLabel,
          ];
        }

        // Layout actual (programas no semester-based).
        const sinMeses = row.needsMonthReview ? 'Sí' : 'No';
        const monthLabels = [];
        for (let month = 1; month <= 12; month++) {
          const m = (row.months || []).find((x) => x.month === month);
          monthLabels.push(monthStatusToLabel(m?.status, month));
        }
        const enrolledSubjectsLabel = Array.isArray(row.enrolledSubjects) && row.enrolledSubjects.length > 0
          ? row.enrolledSubjects.map((s) => s.name).join(', ')
          : '—';
        return [
          row.student?.id ?? '—',
          fullName,
          u.cellphone ?? '—',
          u.email ?? '—',
          row.programName ?? programLabel,
          row.semesterName ?? '—',
          firstPayLabel,
          parentNameVal,
          parentPhoneVal,
          sinMeses,
          enrolledSubjectsLabel,
          gradFeeLabel,
          ...monthLabels,
        ];
      });

      const header = isSemesterBasedExport
        ? [
            'ID',
            'NOMBRES',
            'CELULAR',
            'CORREO',
            'ACUDIENTE',
            'TEL. ACUDIENTE',
            'PROGRAMA',
            'SEMESTRE',
            'FECHA INICIO',
            'ÚLTIMO PAGO',
            'MAT. PAGADAS',
            'MAT. ACTIVADAS',
            'ESTADO FINANCIERO',
            'AL DÍA DERECHOS DE GRADO',
          ]
        : [
            'ID',
            'NOMBRES',
            'CELULAR',
            'CORREO',
            'PROGRAMA',
            'SEMESTRE',
            'FECHA PRIMER PAGO',
            'ACUDIENTE',
            'TELÉFONO ACUDIENTE',
            'SIN MESES',
            'MATERIAS MATRICULADAS',
            'AL DÍA DERECHOS DE GRADO',
            ...MONTH_LABELS,
          ];

      downloadExcel({
        fileName: `reporte-pagos-${dayjs().format('YYYY-MM-DD')}`,
        sheet: 'reporte-pagos',
        tablePayload: { header, body },
      });
    } catch (err) {
      console.error('Error descargando excel reporte de pagos:', err);
    } finally {
      setDownloading(false);
    }
  }, [hasMinimumFilters, filters]);

  const canAccessReport = ['supervisor_admin', 'admin'].includes(user?.role?.type);
  if (!canAccessReport) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          No tienes permisos para acceder a esta sección.
        </Text>
      </Box>
    );
  }

  const pagination = report.meta?.pagination || {};
  const summary = report.meta?.summary || {};
  const isSemesterBased = report.meta?.isSemesterBased === true;
  const total = pagination.total || 0;
  const pageCount = pagination.pageCount || 0;
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <Box p={4} maxW="100%">
      <SmartFilterBar
        title="Filtro Pagos"
        description="Selecciona los filtros para refinar el reporte de cartera."
        value={filters}
        onChange={handleFilterChange}
        loading={loading}
      />

      {!hasMinimumFilters && (
        <Box
          mt={6}
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="gray.200"
          borderStyle="dashed"
          bg="gray.50"
          minH="280px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex flexDirection="column" align="center" gap={4} maxW="360px">
            <Flex
              w={16}
              h={16}
              borderRadius="full"
              bg="#E8E4FC"
              color="#574feb"
              borderWidth="1px"
              borderColor="#D4CCF0"
              align="center"
              justify="center"
            >
              <Box as={FiFilter} boxSize={8} />
            </Flex>
            <Box textAlign="center">
              <Text fontWeight="semibold" fontSize="lg" color="gray.800" mb={2}>
                Selecciona filtros para ver el reporte
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                Elige al menos un <strong>Tipo de programa</strong> en la barra de filtros de arriba para generar la
                matriz de pagos por estudiante y mes.
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {hasMinimumFilters && (
        <>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} my={6}>
            <Tooltip
              label="Cantidad total de estudiantes que coinciden con los filtros seleccionados."
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="blue.100" p={2} borderRadius="full">
                    <Box as={FiUsers} color="blue.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Total Estudiantes
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.total ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label={isSemesterBased
                ? 'Estudiantes al día según su histórico financiero de materias.'
                : 'Estudiantes que no tienen meses vencidos (todos pagados o por vencer).'}
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="green.100" p={2} borderRadius="full">
                    <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Al Día
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.alDia ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label={isSemesterBased
                ? 'Estudiantes en mora: deben materias de meses vencidos.'
                : 'Estudiantes con al menos un mes vencido sin pagar.'}
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="red.100" p={2} borderRadius="full">
                    <Box as={FiAlertTriangle} color="red.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      {isSemesterBased ? 'En Mora' : 'Con Morosidad'}
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.conMorosidad ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label={isSemesterBased
                ? 'Estudiantes a los que solo les falta pagar la materia del mes en curso.'
                : 'Estudiantes con el mes actual por vencer (aún no pagado).'}
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="orange.100" p={2} borderRadius="full">
                    <Box as={FiAlertCircle} color="orange.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Próximos a vencer
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.proximosAVencer ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
          </SimpleGrid>

          <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
            <HStack spacing={4} fontSize="sm" color="gray.600" flexWrap="wrap">
              {isSemesterBased ? (
                <>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="green.400" />
                    <Text>Al día</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="orange.400" />
                    <Text>Próximo a vencer</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="red.400" />
                    <Text>En mora</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="gray.400" />
                    <Text>Sin pagos</Text>
                  </HStack>
                </>
              ) : (
                <>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="green.200" />
                    <Text>Pagado</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="red.200" />
                    <Text>Vencido</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg="orange.200" />
                    <Text>Próximo a vencer</Text>
                  </HStack>
                </>
              )}
            </HStack>
            <Button
              size="sm"
              colorScheme="green"
              isLoading={downloading}
              isDisabled={loading || !report.data.length}
              onClick={handleDownloadExcel}
            >
              Descargar Excel
            </Button>
          </Flex>

          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <Box bg="#574feb" px={5} py={3}>
              <Flex align="center" gap={3}>
                <Box as={BiGridAlt} color="white" boxSize={6} />
                <Text fontWeight="bold" color="white" fontSize="lg">
                  {isSemesterBased
                    ? 'Seguimiento de cartera — pago por materias'
                    : 'Seguimiento mensual de pagos'}
                </Text>
              </Flex>
            </Box>

            {loading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : isSemesterBased ? (
              <CarteraTable data={report.data} onReload={loadReport} />
            ) : (
              <MonthlyTable data={report.data} onReload={loadReport} />
            )}

            <Flex
              justify="space-between"
              align="center"
              p={4}
              borderTopWidth="1px"
              borderColor="gray.100"
              flexWrap="wrap"
              gap={3}
            >
              <Text color="gray.600" fontSize="sm">
                Mostrando {start}-{end} de {total} estudiantes
              </Text>
              {pageCount > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel=">"
                  previousLabel="<"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="reportePagosPagination"
                  pageClassName="reportePagosPaginationItem"
                  activeClassName="reportePagosPaginationActive"
                  previousClassName="reportePagosPaginationItem"
                  nextClassName="reportePagosPaginationItem"
                  disabledClassName="reportePagosPaginationDisabled"
                />
              )}
            </Flex>
          </Box>

          <style>{`
            .reportePagosPagination {
              display: flex;
              list-style: none;
              gap: 4px;
              margin: 0;
              padding: 0;
            }
            .reportePagosPaginationItem {
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 36px;
              height: 36px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              color: #4a5568;
              transition: all 0.2s;
            }
            .reportePagosPaginationItem:hover {
              background-color: #f7fafc;
              border-color: #cbd5e0;
            }
            .reportePagosPaginationItem a {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              padding: 0 12px;
            }
            .reportePagosPaginationActive {
              background-color: #574feb !important;
              border-color: #574feb !important;
              color: white !important;
            }
            .reportePagosPaginationActive a {
              color: white !important;
            }
            .reportePagosPaginationDisabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}</style>
        </>
      )}
    </Box>
  );
}
